import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Activities.css";
import {
  ArrowUpRight,
  Binoculars,
  Building2,
  CalendarDays,
  ChevronDown,
  Compass,
  Headphones,
  Landmark,
  MapPin,
  Palmtree,
  Repeat2,
  Search,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  Users,
  Waves
} from "lucide-react";
import usePageMeta from "../../../hooks/usePageMeta";
import activityOne from "../../../assets/hero-top/activities-1.png";
import activityTwo from "../../../assets/hero-top/activities-2.png";
import activityThree from "../../../assets/hero-top/activities-3.png";
import activityFour from "../../../assets/hero-top/activities-4.png";
import { categoryChips as cityActivityChips } from "./Cities_activities/Cities_activities";
import { internationalChips } from "./Cities_activities/International";
import { useCities } from "../../../services/usecitiesbus";

const activityOptions = ["All Activities", "Adventure", "Water Activities", "City Experiences", "Food & Drinks"];
// const destinationOptions = ["Where to?", "Goa", "Dubai", "Singapore", "Manali", "Jaipur", "Bali"];
const travelerOptions = ["1 Traveler", "2 Travelers", "3 Travelers", "4 Travelers", "5+ Travelers"];

const cityActivityMeta = {
  "New Delhi": { tag: "Capital Heritage Trails", icon: Landmark },
  Jaipur: { tag: "Royal Culture & Forts", icon: Landmark },
  Mumbai: { tag: "City Walks & Sea Views", icon: Building2 },
  Goa: { tag: "Beaches, Food & Fun", icon: Palmtree },
  Ladakh: { tag: "High-Altitude Adventures", icon: Compass },
  Kerala: { tag: "Backwaters & Heritage", icon: Waves },
  Manali: { tag: "Mountain Escapes", icon: Palmtree },
  Andaman: { tag: "Island Paradise", icon: Waves },
};

const trendingDestinations = cityActivityChips.map((city) => ({
  name: city.title,
  code: city.code,
  tag: cityActivityMeta[city.title]?.tag || "Handpicked Indian Experiences",
  icon: cityActivityMeta[city.title]?.icon || MapPin,
  image: city.image,
}));

const internationalActivityMeta = {
  Singapore: { tag: "City of Innovation", icon: Palmtree },
  Bangkok: { tag: "Land of Smiles", icon: Landmark },
  Tokyo: { tag: "Where Tradition Meets Tomorrow", icon: Building2 },
  "Kuala Lumpur": { tag: "Vibrant. Modern. Welcoming.", icon: Building2 },
  Paris: { tag: "Timeless Charm", icon: Landmark },
  London: { tag: "History in Every Step", icon: Landmark },
  Phuket: { tag: "Tropical Paradise", icon: Palmtree },
  Dubai: { tag: "Dream. Discover. Dubai.", icon: Building2 },
};

const internationalDestinations = internationalChips.map((city) => ({
  name: city.title,
  code: city.code,
  tag: internationalActivityMeta[city.title]?.tag || "Global Experiences",
  icon: internationalActivityMeta[city.title]?.icon || MapPin,
  image: city.image,
}));

export default function Activities() {
  usePageMeta(
  "Tours, Activities & Things to Do – MakeBeeTrip",
  "Discover city tours, adventure activities and things to do across India and worldwide. Book sightseeing, attractions and experiences on MakeBeeTrip.",
  "activities booking, things to do, city tours, sightseeing tours, adventure activities, attractions tickets, tour experiences"
);
  const navigate = useNavigate();
  const { cities } = useCities();
  const [activity, setActivity] = useState("All Activities");
  const [destination, setDestination] = useState("Where to?");
  const [date, setDate] = useState("");
  const [travelers, setTravelers] = useState("1 Traveler");

  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: "ease-out-cubic", offset: 50 });
    const timer = setTimeout(() => AOS.refresh(), 80);
    return () => clearTimeout(timer);
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const openIndianCity = (city) => {
    navigate(`/cities-activities?toCode=${city.code}&city=${encodeURIComponent(city.name)}`);
  };
  const openInternationalCity = (city) => {
    navigate(`/international-activities?toCode=${city.code}&city=${encodeURIComponent(city.name)}`);
  };
  return (
    <main className="activities-page">
      <section className="activities-hero-card mbt-animated-card" data-aos="fade-up">
        <div className="activities-hero-top">
          <div className="activities-copy">
            <div className="activities-compass">
              <Compass size={25} />
            </div>
            <div>
              <h1>
                Search <span>Activities</span>
                <Sparkles size={18} />
              </h1>
              <p>Discover amazing experiences, attractions and activities worldwide</p>
            </div>
          </div>

          <div className="activities-collage" aria-hidden="true">
            <span className="activities-bubble activities-bubble--one"><Binoculars size={17} /></span>
            <span className="activities-bubble activities-bubble--two"><Waves size={17} /></span>
            <span className="activities-bubble activities-bubble--three"><Users size={17} /></span>
            <img className="activities-shot activities-shot--one" src={activityOne} alt="" />
            <img className="activities-shot activities-shot--two" src={activityTwo} alt="" />
            <img className="activities-shot activities-shot--three" src={activityThree} alt="" />
            <img className="activities-shot activities-shot--four" src={activityFour} alt="" />
            <div className="activities-dash-line" />
          </div>
        </div>

        <div className="activities-perks">
          <span><Sparkles size={13} /> Curated Experiences</span>
          <span><ShieldCheck size={13} /> Best Price Guarantee</span>
          <span><TicketCheck size={13} /> Instant Confirmation</span>
          <span><Headphones size={13} /> 24/7 Support</span>
        </div>

        <div className="activities-search-card mbt-animated-card" data-aos="fade-up" data-aos-delay="120">
          <div className="activities-field">
            <Users size={19} />
            <div>
              <label>Activity</label>
              <select value={activity} onChange={(e) => setActivity(e.target.value)}>
                {activityOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
            </div>
            <ChevronDown size={15} />
          </div>
          <div className="activities-field">
            <MapPin size={19} />
            <div>
              <label>Destination</label>
              <select value={destination} onChange={(e) => setDestination(e.target.value)}>
                <option>Where to?</option>

    {cities.map((city) => (
  <option key={city.id} value={city.name}>
    {city.name}
  </option>
))}

              </select>
            </div>
            <ChevronDown size={15} />
          </div>
          <button className="activities-swap" aria-label="Swap activity destination">
            <Repeat2 size={17} />
          </button>
          <div className="activities-field">
            <CalendarDays size={19} />
            <div>
              <label>Date</label>
              <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="activities-field">
            <Users size={19} />
            <div>
              <label>Travelers</label>
              <select value={travelers} onChange={(e) => setTravelers(e.target.value)}>
                {travelerOptions.map((item) => <option key={item}>{item}</option>)}
              </select>
            </div>
            <ChevronDown size={15} />
          </div>
          <button className="activities-search-btn">
            <Search size={18} />
            Search Activities
          </button>
        </div>
      </section>

      <section className="activities-trending">
        <div className="activities-trending-head">
          <div>
            <span className="activities-eyebrow"><Sparkles size={13} /> Explore</span>
            <h2>Indian <span>Cities</span></h2>
            <p>Discover India's most loved activity cities and places.</p>
          </div>
        </div>

        <div className="activities-destination-grid">
          {trendingDestinations.map((city, index) => {
            const Icon = city.icon;
            return (
              <article
                className="activities-destination-card mbt-animated-card"
                data-aos="fade-up"
                data-aos-delay={index * 80}
                key={city.name}
                role="button"
                tabIndex={0}
                onClick={() => openIndianCity(city)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  openIndianCity(city);
                }}
              >
                <img className="activities-destination-img" src={city.image} alt={city.name} />
                <span className="activities-destination-icon"><Icon size={22} /></span>
                <div className="activities-destination-copy">
                  <h3>{city.name}</h3>
                  <p>{city.tag}</p>
                </div>
                <button type="button" className="activities-destination-arrow" aria-label={`Explore ${city.name}`}>
                  <ArrowUpRight size={15} />
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="activities-trending activities-trending--international">
        <div className="activities-trending-head">
          <div>
            <span className="activities-eyebrow"><Sparkles size={13} /> Explore</span>
            <h2>International <span>Cities</span></h2>
            <p>Explore every international city listed on the activities page.</p>
          </div>
        </div>

        <div className="activities-destination-grid">
          {internationalDestinations.map((city, index) => {
            const Icon = city.icon;
            return (
              <article
                className="activities-destination-card mbt-animated-card"
                data-aos="fade-up"
                data-aos-delay={index * 80}
                key={city.name}
                role="button"
                tabIndex={0}
                onClick={() => openInternationalCity(city)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  openInternationalCity(city);
                }}
              >
                <img className="activities-destination-img" src={city.image} alt={city.name} />
                <span className="activities-destination-icon"><Icon size={22} /></span>
                <div className="activities-destination-copy">
                  <h3>{city.name}</h3>
                  <p>{city.tag}</p>
                </div>
                <button type="button" className="activities-destination-arrow" aria-label={`Explore ${city.name}`}>
                  <ArrowUpRight size={15} />
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="activities-final-cta">
        <div>
          <h2>Your Adventure Awaits</h2>
          <p>Plan smarter, travel better, and enjoy every moment with handpicked activities designed to match your vibe.</p>
          <button type="button" onClick={() => navigate('/contact')} aria-label="Contact us">
            Start Exploring Now
            <ArrowUpRight size={17} />
          </button>
        </div>
      </section>

    </main>
  );
}
